import { ArrowUpOutlined, PlusOutlined } from '@ant-design/icons';
import { Button } from '@mm/components';

import { useOverflow } from '@mm/hooks/src/use-overflow';
import { useViewActions } from '@mm/state/actions/viewActions';
import classNames from 'classnames';
import React, { ReactNode, useRef, useState } from 'react';
import Slider from 'react-slick';
import { AutomationEmailStats } from '../AutomationEmailStats/AutomationEmailStats';

import { InputTriggerCard } from './InputTriggerCard/InputTriggerCard';
import styles from './InputTriggers.module.css';
import { useInputTriggersIdentification } from './use-input-triggers-identification';
import { useSuggestedInputTriggers } from './use-suggested-input-triggers';

type Props = {
  className?: string;
  onAddInputTriggerClicked: () => void;
  campaignId: number;
  triggers: ReturnType<typeof useInputTriggersIdentification>;
  withTooltip: (children: ReactNode) => ReactNode;
};

const Arrow = props => {
  const { className, onClick, prev, next } = props;
  return (
    <div
      className={classNames(className, styles.arrow, {
        [styles.prev]: prev,
        [styles.next]: next,
      })}
      onClick={onClick}
    >
      <ArrowUpOutlined />
    </div>
  );
};

export const InputTriggers = (props: Props) => {
  const {
    campaignId,
    className,
    onAddInputTriggerClicked,
    triggers,
    withTooltip,
  } = props;

  const [idx, setIdx] = useState(0);

  const ref = useRef<HTMLDivElement>(null);

  const slickTrackEl = ref.current?.children[0]?.children[1];
  const { isOverflowX } = useOverflow(slickTrackEl);

  const viewActions = useViewActions();

  const onEmailPhoneConfigureBtnClick = () => {
    viewActions.showSlideoutMenu('automationEmailSmsSettings', {
      campaignId,
    });
  };

  const sliderOpts = {
    className: styles.slider,
    dots: false,
    infinite: false,
    draggable: false,
    swipe: false,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    variableWidth: true,
    afterChange: setIdx,
    nextArrow: <Arrow next />,
    prevArrow: <Arrow prev />,
  };

  const suggestedTriggers = useSuggestedInputTriggers({ campaignId });
  const triggersList = triggers?.length ? triggers : suggestedTriggers;

  const renderTriggers = triggersList?.map((trigger, i) =>
    withTooltip?.(
      <InputTriggerCard
        campaignId={campaignId}
        type={trigger.type}
        kind={trigger.kind}
        className={classNames(styles.card, trigger.className)}
        key={trigger.audienceId + i}
        title={trigger.label}
        channel={trigger.channel}
        subtitle={trigger.subtitle}
        subtitleImage={trigger.subtitleImage}
        subtitleStyleLight={trigger.subtitleStyleLight}
        tags={trigger.keywords}
        info={`${trigger.viewsCount || 0} contact${
          trigger.viewsCount === 1 ? '' : 's'
        }`}
        pageId={trigger.pageId}
        active={trigger.active}
        error={trigger.error}
        onClick={trigger.onClick}
        onDelete={trigger.delete}
        onRemove={trigger.remove}
        onToggle={trigger.setActive}
        onMount={trigger.onMount}
        showActivateAction={trigger.showActivateAction}
      />,
    ),
  );

  const addBtn = withTooltip?.(
    <Button
      css={{
        container: classNames(styles.add, {
          [styles.addOverflowed]: isOverflowX,
        }),
      }}
      color="transparent-no-shadow"
      leftIcon={<PlusOutlined size={16} />}
      onClick={onAddInputTriggerClicked}
    >
      <span className={styles.addText}>Add Trigger</span>
    </Button>,
  );

  // if you plan to change markup order
  // please update ref child query L42 in concord
  return (
    <div
      className={classNames(className, styles.container, {
        [styles.scrolled]: idx,
        [styles.overflow]: isOverflowX,
      })}
    >
      <div className={styles.listHeader}>
        <div className={styles.listTitle}>Automation Triggers</div>
        <div className={styles.settings}>
          {' '}
          <Button
            css={{ container: styles.secondaryButton }}
            color="secondary-blue"
            onClick={onEmailPhoneConfigureBtnClick}
          >
            Settings
          </Button>
        </div>
      </div>

      <div className={styles.list} ref={ref}>
        <Slider {...sliderOpts}>
          {renderTriggers} {addBtn}
        </Slider>
        <AutomationEmailStats campaignId={campaignId} />
      </div>
    </div>
  );
};
